import React, { useState, useEffect, useCallback, useMemo } from 'react';
import SelectAziende from './SelectAziende';
import CreaMenu from './CreaMenu';
import CreaPacchetto from './CreaPacchetto';
import { BASE_URL } from './config';
import { leggiToken } from './services/sessione';
import CreaTradizioni from './CreaTradizioni';

const ModificaEvento = ({ idEvento, evento: initialEvento, onBack }) => {
  const [selectedAziendaId, setSelectedAziendaId] = useState('');
  const [formData, setFormData] = useState({
    titolo: '',
    descrizione: '',
    dataInizio: '',
    dataFine: '',
    campiMenu: [],
    categorie: [],
    prezzo: 0,
    immagineCopertina: null,
    idEvento: '',
    tipologiaEvento: '',
    categoriaEvento: '',
    posti: 0,
    fileName: null,
    fasceOrarie: [],
    idAzienda: null,
  });

  // Fetch evento all’inizio o quando cambia idEvento
  useEffect(() => {
    const fetchEvento = async (idEvento) => {
      if (!idEvento) return;

      const headers = new Headers({
        'Authorization': 'Bearer ' + leggiToken(),
        'Content-Type': 'application/json',
      });

      try {
        const response = await fetch(`${BASE_URL}/eventi/admin/evento/${idEvento}`, { headers });
        const eventoData = await response.json();

        /* Si usa `portate` (nome + descrizione), la stessa forma che i form
         * si aspettano. `campiMenu` resta come ripiego per compatibilita': ha
         * una struttura diversa e non sempre valorizzata. */
        const portate = Array.isArray(eventoData.portate) && eventoData.portate.length
          ? eventoData.portate
          : (eventoData.campiMenu || []).map((campo) => ({
              nome: campo.categoria || '',
              descrizione: (campo.options || [])
                .map((o) => o.nome)
                .filter(Boolean)
                .join(', '),
            }));

        setFormData((prev) => ({
          ...prev,
          ...eventoData,
          portate,
          campiMenu: portate,
          idEvento,
          idAzienda: eventoData.idAzienda || prev.idAzienda,
        }));
      } catch (error) {
        console.error('Errore caricamento evento:', error);
      }
    };

    fetchEvento(idEvento);
  }, [idEvento]);

  // SelectAziende notifica un oggetto { id, tipologia }, non un id:
  // salvarlo com'era significava spedire un oggetto come idAzienda.
  const handleSelectedAzienda = useCallback((azienda) => {
    setSelectedAziendaId(azienda && azienda.id ? azienda.id : '');
  }, []);

  // Funzione per preparare payload da inviare al backend
  function preparaInsertEventoDTO(data) {
    return {
      titolo: data.titolo,
      descrizione: data.descrizione,
      dataInizio: data.dataInizio ? data.dataInizio.substring(0, 10) : null,
      dataFine: data.dataFine ? data.dataFine.substring(0, 10) : null,
      prezzo: Number(data.prezzo) || 0,
      immagineCopertina: data.immagineCopertina || null,
      categorie: data.categorie || [],
      fasceOrarie: (data.fasceOrarie || []).map((f) => f.replace(/\s*-\s*/, '-')),
      // PortataDTO lato backend ha nome/descrizione/traduzioni: mandare
      // {categoria, options} faceva arrivare nome e descrizione a null, e le
      // portate venivano salvate vuote. Le traduzioni si mandano vuote cosi'
      // il backend le rigenera dal testo attuale (con cache): se l'utente ha
      // modificato una portata, le vecchie traduzioni sarebbero sbagliate.
      campiMenu: (data.campiMenu || []).map((campo) => ({
        nome: campo.nome,
        descrizione: campo.descrizione,
        traduzioni: [],
      })),
      posti: Number(data.posti) || 0,
      fileName: data.fileName || null,
      idAzienda: data.idAzienda || null,
      tipologiaEvento: data.tipologiaEvento || null,
      idEvento: data.idEvento || null,
    };
  }

  // Funzione submit stabile con useCallback
  const handleSubmit = useCallback(
    (dataDaInviare) => {
      const payload = preparaInsertEventoDTO({
        ...formData,
        ...dataDaInviare,
        idAzienda: selectedAziendaId || formData.idAzienda,
      });

      fetch(`${BASE_URL}/eventi/admin/${payload.idEvento}`, {
        method: 'PUT',
        headers: {
          'Authorization': 'Bearer ' + leggiToken(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })
        .then((res) => {
          if (!res.ok) throw new Error('Errore durante aggiornamento evento');
          return res.json();
        })
        .then(() => {
          // Qui potresti resettare formData o notificare successo
        })
        .catch((error) => {
          console.error('Errore aggiornamento evento:', error);
        });
    },
    [formData, selectedAziendaId]
  );

  // Calcola il componente da renderizzare in base a categoriaEvento
  const componenteForm = useMemo(() => {
    if (formData.categoriaEvento === 'food') {
      return <CreaMenu onFormSubmit={handleSubmit} azienda={selectedAziendaId} evento={formData} />;
    }
    if (formData.categoriaEvento === 'tradition') {
      return <CreaTradizioni onFormSubmit={handleSubmit} azienda={selectedAziendaId} evento={formData} />;
    }
    if (formData.categoriaEvento === 'transport' || formData.categoriaEvento === 'solemare') {
      return <CreaPacchetto onFormSubmit={handleSubmit} azienda={selectedAziendaId} evento={formData} />;
    }
    return null;
  }, [formData.categoriaEvento, selectedAziendaId, handleSubmit, formData]);

  return (
    <div className="main-content">
      <div className="crea-evento">

        <div className="crea-evento__passo">
          <h2 className="crea-evento__titolo">Azienda</h2>
          {/* L'evento appartiene gia' a un'azienda: si mostra, non si cerca. */}
          <SelectAziende
            onSelectedData={handleSelectedAzienda}
            idAziendaFissa={formData.idAzienda}
          />
        </div>

        {formData.tipologiaEvento && (
          <p>
            <b>Tipologia evento:</b> {formData.tipologiaEvento}
          </p>
        )}

        {/* .form-evento: il CSS di CreaEvento.css e' scoped su questa classe */}
        <div className="form-evento">{componenteForm}</div>

      </div>
    </div>
  );
};

export default ModificaEvento;
